import { ComponentType, ReactNode, useState } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/sharp-regular-svg-icons";
import { Loader } from "@mantine/core";
import { Toast } from "design-system/src/components/toast";
import {
  AppealSupportingDocumentType,
  ButtonSlotProps,
  DocumentUploadModalMode,
  UploadFileInputSlotProps,
} from "../types";
import { documentTypeDisplayValueMap } from "../document-type-display-values";

import styles from "./upload-document-view.module.scss";

const ACCEPTED_MIME_TYPES = ["application/pdf", "image/png", "image/jpeg"];

const getDefaultDocumentUploadDescription = (
  documentType: AppealSupportingDocumentType,
) => {
  if (documentType === AppealSupportingDocumentType.Other) {
    return "You only need to upload this document if it was suggested by your provider or is required by the denial letter.";
  }

  const documentDisplayValue = documentTypeDisplayValueMap[documentType];
  const doesDocumentTypeStartWithVowel = ["a", "e", "i", "o", "u"].includes(
    documentDisplayValue.charAt(0).toLowerCase(),
  );

  return `You only need to upload ${doesDocumentTypeStartWithVowel ? "an" : "a"} ${documentDisplayValue.toLowerCase()} if it was suggested by your provider or is required by the denial letter.`;
};

const getDocumentUploadDescription = (
  documentType: AppealSupportingDocumentType,
  documentTypeDescriptions:
    | Partial<Record<AppealSupportingDocumentType, string>>
    | undefined,
) => {
  const overrideCopy = documentTypeDescriptions?.[documentType];

  if (overrideCopy) {
    return overrideCopy;
  }

  return getDefaultDocumentUploadDescription(documentType);
};

const getLoadingText = (
  mode: DocumentUploadModalMode,
  isSubmitLoading: boolean,
) => {
  if (!isSubmitLoading) {
    return "Loading Document Files";
  }

  return mode === DocumentUploadModalMode.EDIT
    ? "Updating Document"
    : "Adding Document";
};

type UploadDocumentViewProps = {
  selectedDocumentType: AppealSupportingDocumentType;
  files: File[];
  onBack: () => void;
  onFilesUploaded: (files: File[]) => void;
  onViewFile: (file: File) => void;
  onSaveDocument: () => void;
  onDeleteFile: (file: File) => void;
  error?: string;
  isSubmitLoading: boolean;
  isLoadingInitialState: boolean;
  mode: DocumentUploadModalMode;
  documentTypeDescriptions?: Partial<
    Record<AppealSupportingDocumentType, string>
  >;
  Button: ComponentType<ButtonSlotProps>;
  UploadFileInput: ComponentType<UploadFileInputSlotProps>;
  additionalUploadContent?: ReactNode;
};

export const UploadDocumentView = ({
  selectedDocumentType,
  files,
  onBack,
  onFilesUploaded,
  onViewFile,
  onSaveDocument,
  onDeleteFile,
  error,
  isSubmitLoading,
  isLoadingInitialState,
  mode,
  documentTypeDescriptions,
  Button,
  UploadFileInput,
  additionalUploadContent,
}: UploadDocumentViewProps) => {
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleFilesSelected = (uploadedFiles: File[]) => {
    setValidationError(null);
    onFilesUploaded(uploadedFiles);
  };

  const handleViewFile = (file: File) => {
    setValidationError(null);
    onViewFile(file);
  };

  const handleFileDeleted = (file: File) => {
    setValidationError(null);
    onDeleteFile(file);
  };

  const handleSlotError = (message: string) => {
    setValidationError(message);
  };

  const handleSaveDocument = () => {
    setValidationError(null);
    onSaveDocument();
  };

  return (
    <>
      <h2 className={styles.modalContentHeader}>
        {`Upload your ${selectedDocumentType === AppealSupportingDocumentType.Other ? "document" : documentTypeDisplayValueMap[selectedDocumentType]}`}
      </h2>
      <p className={styles.modalContentDescription}>
        {getDocumentUploadDescription(
          selectedDocumentType,
          documentTypeDescriptions,
        )}
      </p>
      <div className={styles.errorMessage}>
        <Toast variant="error" isVisible={!!(error || validationError)}>
          {error ?? validationError}
        </Toast>
      </div>
      <div
        className={`${styles.fileUploadContainer}${additionalUploadContent && !files.length ? ` ${styles.fileUploadCompact}` : ""}`}
      >
        <UploadFileInput
          onFilesSelected={handleFilesSelected}
          onError={handleSlotError}
          accept={ACCEPTED_MIME_TYPES}
          multiple
          disabled={isSubmitLoading}
          files={files}
          onViewFile={handleViewFile}
          onFileDeleted={handleFileDeleted}
        />
        {(isSubmitLoading || isLoadingInitialState) && (
          <div className={styles.loadingOverlay}>
            <Loader className={styles.loadingSpinner} size={24} />
            <span className={styles.loadingText}>
              {getLoadingText(mode, isSubmitLoading)}
            </span>
          </div>
        )}
      </div>
      {additionalUploadContent}
      <div className={styles.uploadDocumentButtons}>
        <Button
          onClick={onBack}
          type="button"
          variant="tertiary"
          size="medium"
          disabled={isSubmitLoading || isLoadingInitialState}
        >
          <FontAwesomeIcon icon={faArrowLeft as IconDefinition} />
          Back
        </Button>
        <Button
          type="button"
          variant="primary"
          size="medium"
          onClick={handleSaveDocument}
          disabled={!files.length || isSubmitLoading || isLoadingInitialState}
        >
          {mode === DocumentUploadModalMode.EDIT
            ? "Update Document"
            : "Add Document"}
        </Button>
      </div>
    </>
  );
};
